import type { AuthConfig } from './types'

/** GET /api/v1/auth/config (public; MS-977, MS-997). A plain fetch, not
 * apiFetch: it is read around sign-out, and nothing about a failed read here
 * is a session expiry. */
export async function fetchAuthConfig(signal?: AbortSignal): Promise<AuthConfig> {
  const resp = await fetch('/api/v1/auth/config', { credentials: 'include', signal })
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
  const body = (await resp.json()) as Partial<AuthConfig>
  return { logoutUrl: typeof body.logoutUrl === 'string' ? body.logoutUrl : null }
}

// The config is fixed for the life of a backend process, so one successful
// read serves the whole page load: the AppShell reads it once signed in, and
// sign-out takes the cached logoutUrl instead of waiting on another request.
// A failed read is not cached — the next caller tries again. Module-level
// rather than React Query: sign-out must answer without a query client.
let cachedConfig: AuthConfig | null = null
let inflightConfig: Promise<AuthConfig> | null = null

/** The config if it has been read on this page load, else null. */
export function cachedAuthConfig(): AuthConfig | null {
  return cachedConfig
}

/** The config, read once per page load and shared by every caller. */
export function loadAuthConfig(): Promise<AuthConfig> {
  if (cachedConfig) return Promise.resolve(cachedConfig)
  inflightConfig ??= fetchAuthConfig()
    .then((config) => {
      cachedConfig = config
      return config
    })
    .finally(() => {
      inflightConfig = null
    })
  return inflightConfig
}

/** Test-only: forget the cached config between tests. */
export function resetAuthConfigCache(): void {
  cachedConfig = null
  inflightConfig = null
}

export const LOGOUT_CONFIG_TIMEOUT_MS = 5_000

function safeLogoutUrl(logoutUrl: string | null): string | null {
  if (!logoutUrl) return null
  try {
    const { protocol } = new URL(logoutUrl)
    return protocol === 'https:' || protocol === 'http:' ? logoutUrl : null
  } catch {
    return null
  }
}

/** The config from the cache, or from one read bounded by
 * LOGOUT_CONFIG_TIMEOUT_MS when it has not been read on this page load; null
 * when it cannot be read in time. For the callers that must never wait on a
 * hung GET /auth/config: sign-out. */
export async function boundedAuthConfig(): Promise<AuthConfig | null> {
  const cached = cachedAuthConfig()
  if (cached) return cached
  try {
    const config = await fetchAuthConfig(AbortSignal.timeout(LOGOUT_CONFIG_TIMEOUT_MS))
    cachedConfig = config
    return config
  } catch {
    return null
  }
}

/** The Cognito logout URL to leave for after POST /logout, or null for none
 * (an unreadable config, or anything that is not an http(s) URL — the value
 * is the backend's own, but a navigation target is still checked). */
export function providerLogoutUrlOf(config: AuthConfig | null): string | null {
  return config ? safeLogoutUrl(config.logoutUrl) : null
}

/** providerLogoutUrlOf() for the config read through boundedAuthConfig(). */
export async function providerLogoutUrl(): Promise<string | null> {
  return providerLogoutUrlOf(await boundedAuthConfig())
}
